import { api } from '@data/store';
import { transformUserReportsData } from '@data/utils/transform-user-reports-data';
import { DailyReportDefaults, PendingAbsenceEntity, ReportEntity, WorkDayUserEntity } from '@data/utils/types';
import { addDays } from '@app/utils';
import { daysInSprint } from '@app/constants/app';

export const fetchReports = async (
  organizationId: string,
  userId: string,
  params: { from?: string | Date; to?: string | Date; productIds?: string[]; offset?: number; limit?: number },
  hideWeekend: boolean,
  { pageParam },
): Promise<{ from: Date | string; to: Date | string; workDaysData: WorkDayUserEntity[] }> => {
  const newParams = pageParam ? { ...params, ...pageParam } : { ...params };

  const data = await api.get(`organizations/${organizationId}/users/${userId}/reports`, {
    params: newParams,
  });

  const transformedReportsData = transformUserReportsData(data.items, newParams, hideWeekend);

  return {
    from: addDays(new Date(newParams?.from as string), -daysInSprint),
    to: addDays(new Date(newParams?.to as string), -daysInSprint),
    workDaysData: transformedReportsData,
  };
};

/**
 * Every report one person filed in a window, flat and in full. The endpoint takes no limit, so a sprint's
 * worth of reports arrives in one answer; `fetchReports` above asks the same route but buckets the result
 * into days for the Reporting page.
 */
export const fetchUserReportsInRange = async (
  organizationId: string,
  userId: string,
  params: { from: string | Date; to: string | Date },
): Promise<ReportEntity[]> => {
  const data = await api.get(`organizations/${organizationId}/users/${userId}/reports`, { params });

  return data.items ?? [];
};

/**
 * Weekdays in the window the user still owes a report for: the count on the Reporting nav item, and the days
 * themselves, which the report form's calendar marks. `days` are `yyyy-mm-dd` — the day a report belongs to
 * is a calendar day, and an instant would land on the day before for part of the world.
 */
export const fetchUnreportedDayCount = (
  organizationId: string,
  userId: string,
  params: { from: Date | string; to: Date | string },
): Promise<{ count: number; days: string[] }> =>
  api.get(`organizations/${organizationId}/users/${userId}/unreported-days`, { params });

/** The days in the window the user has marked finished despite carrying less than eight hours. */
export const fetchFinishedDays = (
  organizationId: string,
  userId: string,
  params: { from: Date | string; to: Date | string },
): Promise<{ days: string[] }> => api.get(`organizations/${organizationId}/users/${userId}/finished-days`, { params });

export const fetchPendingAbsences = (
  organizationId: string,
  userId: string,
  params: { from: Date | string; to: Date | string },
): Promise<{ absences: PendingAbsenceEntity[] }> =>
  api.get(`organizations/${organizationId}/users/${userId}/pending-absences`, { params });

export const markDayFinished = (organizationId: string, userId: string, workday: string): Promise<void> =>
  api.post(`organizations/${organizationId}/users/${userId}/finished-days/${workday}`, {});

export const markDayUnfinished = (organizationId: string, userId: string, workday: string): Promise<void> =>
  api.delete(`organizations/${organizationId}/users/${userId}/finished-days/${workday}`);

/** What the Phill report form opens with: the chapter it files under, and last time's products. */
export const fetchDailyReportDefaults = (organizationId: string): Promise<DailyReportDefaults> =>
  api.get(`/organizations/${organizationId}/reports/daily-defaults`);

/** The AI draft for one day. Takes seconds: it reads GitHub, your calendar and your Slack posts. */
export const draftDailyReport = (
  organizationId: string,
  values: { productNames: string[]; workday: Date | string },
): Promise<{ drafts: Record<string, string[]> }> =>
  api.post(`/organizations/${organizationId}/reports/daily-draft`, values);

export const createReport = (values: Partial<ReportEntity>): Promise<ReportEntity> => api.post('/reports', values);

export const updateReport = (values: Partial<ReportEntity>): Promise<ReportEntity> =>
  api.patch(`/reports/${values.id}`, values);

export const removeReport = (reportId: string): Promise<void> => api.delete(`/reports/${reportId}`);

export const fetchSummary = async (params: { productId: string; since: string; until: string }) =>
  api.get(`reports/summary`, { params });
